import { useFollowedSite } from '../../../contexts/SiteContext';
import { useEffect, useMemo, useState } from 'react';
import { Package, TrendingUp, TrendingDown, RefreshCw } from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@verlo/shared/components/ui/select';
import { Button } from '@verlo/shared/components/ui/button';
import { reportsIntelApi } from '@verlo/shared/services/api';
import ReportsLayout from '../../../components/reports/ReportsLayout';
import ExplainVarianceBubble from '../../../components/reports/ExplainVarianceBubble';
import SeeMoreLink from '../../../components/reports/SeeMoreLink';
import { SitePicker } from '@verlo/shared/components/ui/site-picker';

const fmtGBP = (n) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(n || 0);
const fmtGBP2 = (n) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 2 }).format(n || 0);

// Heatmap colour intensity (0..1 → emerald shades)
const heatColor = (v, min, max) => {
  if (max === min) return 'rgb(220 252 231)';
  const t = (v - min) / (max - min);
  // emerald 100 → emerald 600
  const r = Math.round(220 - t * 184);
  const g = Math.round(252 - t * 90);
  const b = Math.round(231 - t * 79);
  return `rgb(${r} ${g} ${b})`;
};

const Hero = ({ icon: Icon, label, value, sub, status, testid, kpi, range, siteLabel, sharedContext, seeMoreTo }) => {
  const meta = {
    good: { dot: 'bg-signal-ok-line', cls: 'text-signal-ok bg-signal-ok-tint border-signal-ok-line', label: 'On target' },
    warn: { dot: 'bg-signal-warn-line', cls: 'text-signal-warn bg-signal-warn-tint border-signal-warn-line', label: 'Watch' },
    bad:  { dot: 'bg-signal-stop-line', cls: 'text-signal-stop bg-signal-stop-tint border-signal-stop-line', label: 'Critical' },
    neutral: { dot: 'bg-ink-faint', cls: 'text-ink-soft bg-surface-2 border-hairline', label: 'Live' },
  }[status || 'neutral'];
  return (
    <div className="bg-white border border-hairline rounded-lg p-5 relative group hover:border-ink-faint hover:shadow-[0_8px_30px_-12px_rgba(32,27,95,0.18)] transition-all flex flex-col" data-testid={testid}>
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <Icon size={13} className="text-ink-faint" />
          <span className="text-[11px] uppercase tracking-[0.15em] font-semibold text-ink-faint">{label}</span>
        </div>
        <span className={`inline-flex items-center gap-1 text-[11px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded border ${meta.cls}`}>
          <span className={`w-1 h-1 rounded-full ${meta.dot}`} />
          {meta.label}
        </span>
      </div>
      <div className="font-mono-display text-3xl md:text-4xl font-medium text-ink">{value}</div>
      {sub && <p className="text-[12.5px] text-ink-faint mt-1">{sub}</p>}
      {kpi && <ExplainVarianceBubble kpi={kpi} range={range} siteLabel={siteLabel} context={sharedContext} />}
      {seeMoreTo && (
        <div className="mt-3 pt-3 border-t border-hairline flex justify-end">
          <SeeMoreLink to={seeMoreTo} what={label} testId={`${testid}-see-more`} />
        </div>
      )}
    </div>
  );
};

const StockMarginPage = () => {
  const [sites, setSites] = useState([]);
  // '' = every site this user can read; the API omits the param (S1-4).
  const [site, setSite] = useFollowedSite('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const siteLabel = useMemo(() => sites.find((s) => s.id === site)?.name || site, [sites, site]);

  useEffect(() => { reportsIntelApi.listSites().then((r) => setSites(r.data.sites || [])).catch(() => {}); }, []);

  const load = async () => {
    setLoading(true);
    try { const res = await reportsIntelApi.getStockMargin(site); setData(res.data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [site]);

  const sharedContext = data ? {
    actual_gp_pct: data.totals.actual_gp_pct,
    theoretical_gp_pct: data.totals.theoretical_gp_pct,
    variance_pp: data.totals.variance_pp,
    wastage_pct: data.totals.wastage_pct,
    site_id: site,
  } : null;

  return (
    <ReportsLayout>
      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <SitePicker sites={sites} value={site} onChange={setSite} allowAll allValue="" triggerClassName="h-9 w-[230px] bg-white border-hairline" testId="stock-site-select" />
        <Button variant="outline" size="sm" className="h-9 gap-1.5" onClick={() => { setRefreshing(true); load().finally(() => setRefreshing(false)); }} data-testid="stock-refresh-btn">
          <RefreshCw size={13} className={refreshing ? 'animate-spin' : ''} />
          Refresh
        </Button>
      </div>

      {loading || !data ? (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
          {Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-32 bg-white border border-hairline rounded-lg animate-pulse" />)}
        </div>
      ) : data.is_empty ? (
        <div className="bg-white border border-hairline rounded-lg p-12 text-center" data-testid="stock-empty">
          <Package size={28} className="text-ink-faint mx-auto mb-3" />
          <h3 className="text-[14px] font-semibold text-ink">No stock or margin data yet</h3>
          <p className="text-[12px] text-ink-soft mt-1">No orders in the last 7 days for this site.</p>
        </div>
      ) : (
        <>
          {/* Hero KPIs */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5 mb-6">
            <Hero
              testid="stock-hero-actual-gp"
              icon={TrendingUp}
              label="Actual GP"
              value={`${data.totals.actual_gp_pct}%`}
              sub={`${fmtGBP(data.totals.actual_gp)} this week`}
              status={data.totals.variance_pp > 5 ? 'bad' : data.totals.variance_pp > 2 ? 'warn' : 'good'}
              kpi={{ key: 'actual_gp_pct', label: 'Actual GP %', value: data.totals.actual_gp_pct, target: data.totals.theoretical_gp_pct, format: 'percent' }}
              range="weekly" siteLabel={siteLabel} sharedContext={sharedContext}
            />
            <Hero
              testid="stock-hero-theoretical-gp"
              icon={TrendingUp}
              label="Theoretical GP"
              value={`${data.totals.theoretical_gp_pct}%`}
              sub={`${fmtGBP(data.totals.theoretical_gp)} target`}
              status="neutral"
            />
            <Hero
              testid="stock-hero-variance"
              icon={TrendingDown}
              label="GP variance"
              value={`-${data.totals.variance_pp}pp`}
              sub={`Leaking ${fmtGBP(data.totals.variance_gp)}/week`}
              status={data.totals.variance_pp > 5 ? 'bad' : data.totals.variance_pp > 2 ? 'warn' : 'good'}
              kpi={{ key: 'gp_variance', label: 'GP variance (pp)', value: data.totals.variance_pp, format: 'percent' }}
              range="weekly" siteLabel={siteLabel} sharedContext={sharedContext}
            />
            <Hero
              testid="stock-hero-wastage"
              icon={Package}
              label="Wastage"
              value={`${data.totals.wastage_pct}%`}
              sub={`${fmtGBP(data.totals.wastage_total)} · ${data.totals.wastage_events} events`}
              status={data.totals.wastage_pct > 2 ? 'warn' : 'good'}
              kpi={{ key: 'wastage_pct', label: 'Wastage %', value: data.totals.wastage_pct, target: 2.5, format: 'percent' }}
              range="weekly" siteLabel={siteLabel} sharedContext={sharedContext}
            />
          </div>

          {/* Category margins (real recipe costs vs menu prices) */}
          <div className="bg-white border border-hairline rounded-lg overflow-hidden mb-6" data-testid="stock-categories">
            <div className="px-5 py-4 border-b border-hairline flex items-center justify-between">
              <div>
                <h3 className="text-[13px] font-semibold text-ink">Category margins</h3>
                <p className="text-[12.5px] text-ink-soft">Actual vs theoretical GP · real recipe costs</p>
              </div>
              <div className="text-right">
                <p className="text-[11px] uppercase tracking-wider text-ink-faint font-semibold">Wastage this week</p>
                <p className="font-mono-display text-[14px] text-ink" data-testid="stock-wastage-total">
                  {fmtGBP2(data.totals.wastage_total)} <span className="text-[12.5px] text-ink-faint">· {data.totals.wastage_events} events</span>
                </p>
              </div>
            </div>
            <table className="w-full text-[12px]">
              <thead>
                <tr className="text-[11px] uppercase tracking-wider text-ink-faint border-b border-hairline">
                  <th className="text-left font-semibold px-5 py-2">Category</th>
                  <th className="text-right font-semibold px-3 py-2">Revenue</th>
                  <th className="text-right font-semibold px-3 py-2">Actual GP</th>
                  <th className="text-right font-semibold px-3 py-2">Theoretical GP</th>
                  <th className="text-right font-semibold px-5 py-2">Variance</th>
                </tr>
              </thead>
              <tbody>
                {data.categories.map((c) => (
                  <tr key={c.category} className="border-b border-hairline last:border-0" data-testid="stock-category-row" data-category={c.category}>
                    <td className="px-5 py-2.5 text-ink font-semibold">{c.category}</td>
                    <td className="text-right px-3 py-2.5 font-mono-display text-ink-soft">{fmtGBP(c.revenue)}</td>
                    <td className="text-right px-3 py-2.5 font-mono-display font-semibold text-ink" data-testid={`stock-margin-${c.category.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>{c.actual_gp_pct}%</td>
                    <td className="text-right px-3 py-2.5 font-mono-display text-ink-soft">{c.theoretical_gp_pct}%</td>
                    <td className={`text-right px-5 py-2.5 font-mono-display font-semibold ${c.variance_pp > 5 ? 'text-signal-stop' : c.variance_pp > 2 ? 'text-signal-warn' : 'text-signal-ok'}`}>-{c.variance_pp}pp</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Margin heatmap + top items */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-5 mb-6">
            <div className="bg-white border border-hairline rounded-lg p-5" data-testid="stock-heatmap">
              <h3 className="text-[13px] font-semibold text-ink">7-day margin heatmap</h3>
              <p className="text-[12.5px] text-ink-soft mb-3">GP £ contribution per category per day</p>
              <div className="overflow-x-auto">
                <table className="text-[12.5px] w-full">
                  <thead>
                    <tr>
                      <th className="text-left py-1 px-1 text-[11px] uppercase tracking-wider text-ink-faint font-semibold">Category</th>
                      {data.heatmap.days.map((d, i) => <th key={i} className="text-center py-1 px-1 text-[11px] uppercase tracking-wider text-ink-faint font-semibold w-16">{d}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {data.heatmap.matrix.map((row) => {
                      const min = Math.min(...row.values);
                      const max = Math.max(...row.values);
                      return (
                        <tr key={row.category} data-testid={`heatmap-row-${row.category.toLowerCase()}`}>
                          <td className="py-1 px-1 font-semibold text-ink-soft">{row.category}</td>
                          {row.values.map((v, i) => (
                            <td key={i} className="py-1 px-0.5" style={{ background: heatColor(v, min, max) }} title={`${row.category} · ${data.heatmap.days[i]}: ${fmtGBP(v)}`}>
                              <span className="block text-center font-mono-display text-[12.5px] text-ink-soft px-1 py-1.5">{fmtGBP(v)}</span>
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="bg-white border border-hairline rounded-lg overflow-hidden" data-testid="stock-top-items">
              <div className="px-5 py-4 border-b border-hairline">
                <h3 className="text-[13px] font-semibold text-ink">Top 10 by revenue</h3>
                <p className="text-[12.5px] text-ink-soft">Gross margin per dish</p>
              </div>
              <table className="w-full text-[12px]">
                <thead>
                  <tr className="text-[11px] uppercase tracking-wider text-ink-faint border-b border-hairline">
                    <th className="text-left font-semibold px-5 py-2">Item</th>
                    <th className="text-right font-semibold px-3 py-2">Qty</th>
                    <th className="text-right font-semibold px-3 py-2">Revenue</th>
                    <th className="text-right font-semibold px-5 py-2">GP %</th>
                  </tr>
                </thead>
                <tbody>
                  {data.top_items.map((i) => (
                    <tr key={i.name} className="border-b border-hairline last:border-0">
                      <td className="px-5 py-2.5">
                        <p className="text-ink">{i.name}</p>
                        <p className="text-[11px] text-ink-faint uppercase tracking-wider">{i.category}</p>
                      </td>
                      <td className="text-right px-3 py-2.5 font-mono-display text-ink-soft">{i.qty}</td>
                      <td className="text-right px-3 py-2.5 font-mono-display text-ink">{fmtGBP(i.rev)}</td>
                      <td className="text-right px-5 py-2.5 font-mono-display text-ink">{i.actual_gp_pct}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </ReportsLayout>
  );
};

export default StockMarginPage;
